import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, ArrowLeft } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import Logo from "@/components/Logo";

const inputCls =
  "w-full bg-[#09090D] border border-zinc-800 focus:border-amber-500/60 focus:outline-none rounded-xl px-4 py-3 text-sm text-zinc-100 placeholder:text-zinc-600 transition-colors duration-300";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/admin");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="min-h-screen bg-[#09090D] flex items-center justify-center px-5 relative overflow-hidden">
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse 50% 40% at 50% 0%, rgba(157,78,221,0.16), transparent 65%)",
        }}
      />
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="relative w-full max-w-md"
      >
        <div className="rounded-2xl border border-amber-500/20 bg-[#161622]/90 backdrop-blur-md p-8">
          <div className="flex justify-center mb-8">
            <Logo size={48} />
          </div>
          <h1 className="font-display text-2xl font-bold uppercase tracking-tight text-center mb-1">
            Espace admin
          </h1>
          <p className="text-sm text-zinc-500 text-center mb-8">
            Demandes de concert & prospection collectivités
          </p>

          <form data-testid="login-form" onSubmit={submit} className="space-y-4">
            <div>
              <label className="block text-xs font-jbmono uppercase tracking-widest text-zinc-400 mb-2">Email</label>
              <input
                data-testid="login-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@forty-one.fr"
                className={inputCls}
              />
            </div>
            <div>
              <label className="block text-xs font-jbmono uppercase tracking-widest text-zinc-400 mb-2">
                Mot de passe
              </label>
              <input
                data-testid="login-password-input"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className={inputCls}
              />
            </div>

            {error && (
              <p data-testid="login-error-message" className="text-sm text-red-400 bg-red-500/10 border border-red-500/25 rounded-xl px-4 py-3">
                {error}
              </p>
            )}

            <button
              data-testid="login-submit-button"
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2.5 bg-amber-500 hover:bg-amber-400 disabled:opacity-60 text-white font-bold uppercase tracking-wider text-sm rounded-xl px-6 py-4 transition-colors duration-300"
            >
              {loading && <Loader2 size={16} className="animate-spin" />}
              {loading ? "Connexion…" : "Se connecter"}
            </button>
          </form>
        </div>

        <Link
          data-testid="login-back-link"
          to="/"
          className="flex items-center justify-center gap-2 text-xs text-zinc-500 hover:text-amber-400 transition-colors duration-300 mt-6"
        >
          <ArrowLeft size={13} />
          Retour au site
        </Link>
      </motion.div>
    </main>
  );
}
